import { useState, useEffect } from 'react';
import ReportsService from '@/services/reportsService';
import useBranch from '@/hooks/useBranch';
function normalizeApiDate(value) {
  if (!value) return '';

  const parts = value.split('-');

  // if dd-MM-yyyy → convert
  if (parts[0].length === 2) {
    const [dd, mm, yyyy] = parts;
    return `${yyyy}-${mm}-${dd}`;
  }

  // already correct
  return value;
}

function extractList(payload) {
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.data,
    payload?.items,
    payload?.rows,
    payload?.content,
    payload?.result,
    payload?.reportItems,
    payload?.details,
    payload?.records,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
}

function extractSummary(payload) {
  return payload?.summary || payload?.meta || payload?.totals || payload?.statistics || payload?.stats || {};
}

function extractBreakdown(payload) {
  return payload?.breakdown || payload?.categoryWiseVariance || payload?.categoryBreakdown || payload?.categorySummary || [];
}

/**
 * Custom hook to fetch consumption-wise sales report from the API.
 * @param {Object} params - { startDate, endDate, categoryId }
 * @returns {Object} { data, loading, error, refetch }
 */
export default function useConsumptionReport({ startDate, endDate, categoryId }) {
  const { branch, loading: branchLoading } = useBranch();
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({});
  const [breakdown, setBreakdown] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    const normalizedStartDate = normalizeApiDate(startDate);
    const normalizedEndDate = normalizeApiDate(endDate);

    const isValidDate = (date) => /^\d{4}-\d{2}-\d{2}$/.test(date);
    if (!isValidDate(normalizedStartDate) || !isValidDate(normalizedEndDate)) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = {
        startDate: normalizedStartDate,
        endDate: normalizedEndDate,
        offset: 0,
        limit: 1000,
      };

      if (branch?.id) {
        params.branchId = branch.id;
      }

      if (categoryId !== undefined && categoryId !== null && categoryId !== '') {
        params.categoryId = categoryId;
      }

      const response = await ReportsService.getConsumptionWiseSales(params);

      const payload = response || {};
      console.log('consumption-wise-sales response shape:', {
        keys: Object.keys(payload || {}),
        hasData: Array.isArray(payload?.data),
        hasItems: Array.isArray(payload?.items),
        hasRows: Array.isArray(payload?.rows),
        hasSummary: !!payload?.summary,
        hasBreakdown: Array.isArray(payload?.breakdown) || Array.isArray(payload?.categoryWiseVariance) || Array.isArray(payload?.categoryBreakdown),
      });
      setData(extractList(payload));
      setSummary(extractSummary(payload));
      setBreakdown(extractBreakdown(payload));
    } catch (err) {
      setError(err.message || 'Failed to fetch consumption report');
      setData([]);
      setSummary({});
      setBreakdown([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branch, startDate, endDate, categoryId]);

  return { data, summary, breakdown, loading: loading || branchLoading, error, refetch: fetchData };
}
